import React from "react";

import { Eyebrow, Title } from "../components/CommonComponents";
import MetaInfo from "../components/MetaInfo";
import Header from "../components/Header";
import Footer from "../components/Footer";

import academicstudent from "../assets/img/hero-banners/academic-philosophy.png";

const principles = [
    {
        number: "01",
        title: "Excellence in Education",
        label: "Academic Excellence",
        description:
            "We provide high-quality education meeting international standards through rigorous curriculum, experienced faculty, and focus on critical thinking and creativity.",
    },
    {
        number: "02",
        title: "Student-Centred Learning",
        label: "Learner First",
        description:
            "We prioritize each student's needs and potential, fostering active learning and personal growth through personalized support and mentorship.",
    },
    {
        number: "03",
        title: "Interdisciplinary Collaboration",
        label: "Connected Learning",
        description:
            "We promote collaboration across fields to explore diverse perspectives and develop comprehensive solutions to complex issues.",
    },
    {
        number: "04",
        title: "Research and Innovation",
        label: "Knowledge Creation",
        description:
            "We advance knowledge through cutting-edge research addressing real-world challenges and contributing to societal advancement.",
    },
    {
        number: "05",
        title: "Ethical & Social Responsibility",
        label: "Responsible Citizenship",
        description:
            "We instill strong ethical values and commitment to sustainability, equity, and community service in our students.",
    },
    {
        number: "06",
        title: "Global Perspective",
        label: "World View",
        description:
            "We prepare students for an interconnected world through international curriculum perspectives and global partnerships.",
    },
    {
        number: "07",
        title: "Lifelong Learning",
        label: "Continuous Growth",
        description:
            "We promote continuous personal and professional development to help graduates adapt to evolving career demands.",
    },
];

export default function AcademicPhilosophy() {
    return (
        <>
            <MetaInfo param="" id="about" />
            <Header school="common" />

            <main className="bg-white text-[#132337]">
                {/* HERO */}
                <section className="relative min-h-[60vh] overflow-hidden bg-[#071A2F] text-white lg:min-h-[60vh]">
                    {/* BACKGROUND IMAGE */}
                    <img src={academicstudent} alt="ISBM University academic philosophy" className="absolute inset-0 h-full w-full object-cover" />
                    {/* OVERLAYS */}
                    <div className="absolute inset-0 bg-[#071A2F]/35" />
                    <div className="absolute inset-0 bg-gradient-to-r from-[#071A2F] via-[#071A2F]/90 to-[#071A2F]/15" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#071A2F]/80 via-transparent to-[#071A2F]/20" />
                    {/* UNIQUE PHILOSOPHY GRAPHIC */}
                    <div className="pointer-events-none absolute right-[7%] top-[25%] hidden h-[330px] w-[330px] lg:block">
                        <div className="absolute inset-0 rounded-full border border-white/10" />
                        <div className="absolute inset-[45px] rounded-full border border-white/10" />
                        <div className="absolute inset-[90px] rounded-full border border-[#D5AD57]/25" />
                        <div className="absolute left-1/2 top-0 h-full w-px bg-white/[0.07]" />
                        <div className="absolute left-0 top-1/2 h-px w-full bg-white/[0.07]" />
                        <div className="absolute left-1/2 top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rotate-45 bg-[#8B1E3F]" />
                        <span className="absolute left-1/2 top-[18px] -translate-x-1/2 text-[9px] font-bold uppercase tracking-[0.15em] text-white/50">
                            Learn
                        </span>
                        <span className="absolute bottom-[18px] left-1/2 -translate-x-1/2 text-[9px] font-bold uppercase tracking-[0.15em] text-white/50">
                            Grow
                        </span>
                        <span className="absolute left-[8px] top-1/2 -translate-y-1/2 -rotate-90 text-[9px] font-bold uppercase tracking-[0.15em] text-white/50">
                            Explore
                        </span>
                        <span className="absolute right-[-5px] top-1/2 -translate-y-1/2 rotate-90 text-[9px] font-bold uppercase tracking-[0.15em] text-white/50">
                            Contribute
                        </span>
                    </div>

                    {/* CONTENT */}
                    <div className="relative mx-auto flex min-h-[60vh] w-[min(1380px,calc(100%-48px))] items-end pb-20 pt-[190px] max-sm:w-[calc(100%-28px)] lg:min-h-[60vh] lg:pb-24">
                        <div className="max-w-[900px]">
                            {/* EYEBROW */}
                            <div className="flex items-center gap-4 text-sm font-bold uppercase tracking-[0.18em] text-[#D5AD57]">
                                <span className="h-px w-12 bg-[#D5AD57]" />
                                Academic Philosophy

                            </div>

                            {/* H1 */}
                            <h1 className="mt-7 font-['Libre_Caslon_Display',Georgia,serif] text-[clamp(50px,6.5vw,92px)] font-normal leading-[0.96] tracking-[-0.045em]">
                                Education that shapes

                                <span className="block italic text-[#D5AD57]">
                                    how we think.
                                </span>
                            </h1>

                            {/* INTRO */}
                            <p className="mt-8 max-w-[690px] text-[17px] leading-8 text-white/70">
                                ISBM University aims to help students realize dreams through an educational pedagogy which focuses better on connecting with them as people, inside and outside the classroom.
                            </p>

                            {/* PHILOSOPHY KEYWORDS */}
                            <div className="mt-10 flex flex-wrap items-center gap-x-7 gap-y-4">
                                {[
                                    "Curiosity",
                                    "Knowledge",
                                    "Character",
                                    "Purpose",
                                ].map((item, index) => (
                                    <React.Fragment key={item}>
                                        <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-white/70">
                                            {item}
                                        </span>
                                        {index !== 3 && (
                                            <span className="h-1 w-1 rotate-45 bg-[#D5AD57]" />
                                        )}
                                    </React.Fragment>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* BOTTOM DESCRIPTOR */}
                    <div className="absolute bottom-0 right-0 hidden border-l border-t border-white/15 bg-[#071A2F]/80 backdrop-blur-md lg:block">
                        <div className="flex items-center gap-8 px-9 py-6">
                            <span className="text-xs font-bold uppercase tracking-[0.16em] text-[#D5AD57]">
                                Our Approach
                            </span>
                            <span className="h-8 w-px bg-white/20" />
                            <span className="text-sm text-white/60">
                                Learn · Explore · Grow · Contribute
                            </span>
                        </div>
                    </div>
                </section>

                {/* OUR EDUCATIONAL APPROACH */}
                <section className="bg-white py-20 sm:py-24 lg:py-28">
                    <div className="mx-auto w-[min(1380px,calc(100%-48px))] max-sm:w-[calc(100%-28px)]">
                        <div className="grid gap-14 lg:grid-cols-[.7fr_1.3fr] lg:gap-20">
                            {/* LEFT */}
                            <div>
                                <Eyebrow>
                                    Our Educational Approach
                                </Eyebrow>
                                <Title before="Learning Built Around" highlight="People & Possibility" after="" position="middle" theme="burgundy" />

                                <div className="mt-9 border-l-[3px] border-[#8B1E3F] pl-6">
                                    <p className="font-['Libre_Caslon_Display',Georgia,serif] text-[24px] leading-[1.4] text-[#071A2F]">
                                        The heart of education remains the relationship between faculty, learners and the possibilities they create together.
                                    </p>
                                </div>
                            </div>

                            {/* RIGHT */}
                            <div className="lg:pt-6">
                                <p className="font-['Libre_Caslon_Display',Georgia,serif] text-[clamp(27px,3vw,40px)] font-normal leading-[1.35] text-[#071A2F]">
                                    ISBM University aims to help students realize dreams through an educational pedagogy which focuses better on connecting with them as people, inside and outside the classroom.
                                </p>

                                <div className="mt-10 grid gap-8 md:grid-cols-2">
                                    <div className="border-t border-[#071A2F]/15 pt-6">
                                        <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#8B1E3F]">
                                            Evolving Methods
                                        </span>
                                        <p className="mt-4 text-[15px] leading-8 text-[#65727E]">
                                            With many experiences enriched over past quite many years, our tools of teaching have changed considerably, but the heart of what we do has just remained constant. The relationships that our faculty members look forward to build with the learners take ISBM University education far beyond merely imparting knowledge and preparing job-seekers.
                                        </p>
                                    </div>

                                    <div className="border-t border-[#071A2F]/15 pt-6">
                                        <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#8B1E3F]">
                                            Purpose Beyond Careers
                                        </span>
                                        <p className="mt-4 text-[15px] leading-8 text-[#65727E]">
                                            Our faculties are surely experts in their respective fields, but seek to produce graduates who will aspire to have more than a successful career. According to Chancellor Dr. Vinay Agrawal, ISBM University's students can make a difference in the University itself much before they go out making difference in the real world.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* PHILOSOPHY STATEMENT */}
                <section className="bg-[#071A2F] py-16 text-white sm:py-20">
                    <div className="mx-auto grid w-[min(1380px,calc(100%-48px))] gap-8 max-sm:w-[calc(100%-28px)] lg:grid-cols-[.42fr_1.58fr] lg:items-center">
                        <div>
                            <span className="font-['Libre_Caslon_Display',Georgia,serif] text-[78px] font-normal leading-none text-[#D5AD57]">
                                07
                            </span>
                            <span className="mt-3 block text-[9px] font-bold uppercase tracking-[0.17em] text-white/40">
                                Guiding Principles
                            </span>
                        </div>

                        <p className="max-w-[950px] font-['Libre_Caslon_Display',Georgia,serif] text-[clamp(28px,3.4vw,46px)] font-normal leading-[1.3]">
                            The academic philosophy of ISBM University is rooted in a commitment to
                            <span className="text-[#D5AD57]"> excellence, innovation, and holistic development.</span>
                        </p>
                    </div>
                </section>

                {/* GUIDING PRINCIPLES */}
                <section className="bg-[#F7F2E8] py-20 sm:py-24 lg:py-28">
                    <div className="mx-auto w-[min(1380px,calc(100%-48px))] max-sm:w-[calc(100%-28px)]">
                        {/* HEADER */}
                        <div className="grid gap-10 pb-11 lg:grid-cols-[1fr_430px] lg:items-end">
                            <div>
                                <Eyebrow>
                                    Our Guiding Principles
                                </Eyebrow>
                                <Title before="The Ideas That Shape" highlight="How We Educate" after="" position="middle" theme="burgundy" />
                            </div>

                            <p className="text-[15px] leading-8 text-[#65727E]">
                                The academic philosophy of ISBM University is rooted in a commitment to excellence, innovation, and holistic development.
                            </p>
                        </div>

                        {/* PRINCIPLES MATRIX */}
                        <div className="mt-14 border-t border-[#071A2F]/15">
                            {principles.map((principle, index) => (
                                <article key={principle.number} className="group grid gap-6 border-b border-[#071A2F]/15 py-8 md:grid-cols-[85px_330px_1fr] md:items-start">
                                    {/* NUMBER */}
                                    <div>
                                        <span className={`grid h-[62px] w-[62px] place-items-center font-['Libre_Caslon_Display',Georgia,serif] text-[21px] ${index === 1 || index === 6 ? "bg-[#8B1E3F] text-white" : "border border-[#071A2F]/15 bg-white text-[#8B1E3F]"}`}>
                                            {principle.number}
                                        </span>
                                    </div>

                                    {/* TITLE */}
                                    <div>
                                        <span className="text-[9px] font-bold uppercase tracking-[0.16em] text-[#8B1E3F]">
                                            {principle.label}
                                        </span>
                                        <h3 className="mt-3 font-['Libre_Caslon_Display',Georgia,serif] text-[27px] font-normal leading-[1.15] text-[#071A2F]">
                                            {principle.title}
                                        </h3>
                                    </div>

                                    {/* DESCRIPTION */}
                                    <div>
                                        <p className="max-w-[700px] text-[14px] leading-7 text-[#65727E]">
                                            {principle.description}
                                        </p>

                                        <div className="mt-6 h-px w-full bg-[#071A2F]/5">
                                            <div className="h-px w-0 bg-[#8B1E3F] transition-all duration-500 group-hover:w-full" />
                                        </div>
                                    </div>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                {/* HOW PHILOSOPHY CONNECTS */}
                <section className="bg-white py-20 sm:py-24 lg:py-28">
                    <div className="mx-auto w-[min(1380px,calc(100%-48px))] max-sm:w-[calc(100%-28px)]">
                        <div className="grid gap-14 lg:grid-cols-[.68fr_1.32fr] lg:gap-20">
                            <div>
                                <Eyebrow>
                                    Our Learning Philosophy
                                </Eyebrow>
                                <Title before="From Knowledge to" highlight="Meaningful Impact" after="" position="middle" theme="burgundy" />
                            </div>

                            <div className="grid gap-px bg-[#071A2F]/10 sm:grid-cols-2">
                                {[
                                    ["01", "Learn", "Build strong academic foundations and develop the ability to think critically."],
                                    ["02", "Explore", "Engage with ideas across disciplines, research and real-world situations."],
                                    ["03", "Grow", "Develop professionally and personally through mentorship and active learning."],
                                    ["04", "Contribute", "Use knowledge, values and capabilities to make a meaningful difference."],
                                ].map(([number, title, text], index) => (
                                    <article key={number} className={`min-h-[240px] p-7 lg:p-8 ${index === 3 ? "bg-[#071A2F] text-white" : index === 1 ? "bg-[#EEF4F8]" : index === 2 ? "bg-[#F4E8EC]" : "bg-[#F7F2E8]"}`}>
                                        <span className={`font-['Libre_Caslon_Display',Georgia,serif] text-[29px] ${index === 3 ? "text-[#D5AD57]" : "text-[#8B1E3F]"}`}>
                                            {number}
                                        </span>
                                        <h3 className="mt-7 font-['Libre_Caslon_Display',Georgia,serif] text-[26px] font-normal">
                                            {title}
                                        </h3>
                                        <p className={`mt-4 text-[14px] leading-7 ${index === 3 ? "text-white/55" : "text-[#65727E]"}`}>
                                            {text}
                                        </p>
                                    </article>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                {/* CHANCELLOR MESSAGE */}
                <section className="relative overflow-hidden bg-[#8B1E3F] py-20 text-white sm:py-24 lg:py-28">
                    <div className="pointer-events-none absolute -right-[140px] top-[-160px] h-[520px] w-[520px] rounded-full border border-white/[0.06]" />
                    <div className="mx-auto grid w-[min(1380px,calc(100%-48px))] gap-12 max-sm:w-[calc(100%-28px)] lg:grid-cols-[270px_1fr] lg:gap-20">
                        {/* LABEL */}
                        <div>
                            <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/50">
                                Chancellor's Perspective
                            </span>
                            <h2 className="mt-6 font-['Libre_Caslon_Display',Georgia,serif] text-[38px] font-normal leading-[1.1]">
                                Education should transform lives.
                            </h2>
                        </div>

                        {/* QUOTE */}
                        <div className="relative border-l border-white/20 pl-8 sm:pl-12">
                            <span className="absolute -left-1 top-[-32px] font-['Libre_Caslon_Display',Georgia,serif] text-[120px] leading-none text-white/[0.08]">
                                “
                            </span>
                            <blockquote>
                                <p className="relative font-['Libre_Caslon_Display',Georgia,serif] text-[clamp(20px,3vw,30px)] font-normal leading-[1.45] text-white">
                                    "At ISBM University, we believe education should transform lives. Our students don't just prepare for careers - they develop the vision to make meaningful contributions to society. The difference begins here on campus, where we encourage every student to think beyond themselves and consider how they can serve their communities."
                                </p>
                            </blockquote>

                            <div className="mt-8 border-t border-white/20 pt-6">
                                <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-white/55">
                                    Chancellor
                                </span>
                                <p className="mt-2 font-['Libre_Caslon_Display',Georgia,serif] text-[23px]">
                                    Dr. Vinay Agrawal
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* CLOSING */}
                <section className="bg-[#071A2F] text-white">
                    <div className="mx-auto grid w-[min(1380px,calc(100%-48px))] max-sm:w-[calc(100%-28px)] lg:grid-cols-[.7fr_1.3fr]">
                        <div className="border-b border-white/10 py-16 lg:border-b-0 lg:border-r lg:py-20 lg:pr-14">
                            <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#D5AD57]">
                                Academic Philosophy
                            </span>
                            <h2 className="mt-5 font-['Libre_Caslon_Display',Georgia,serif] text-[38px] font-normal leading-[1.1]">
                                Learn deeply. Grow fully. Contribute meaningfully.
                            </h2>
                        </div>

                        <div className="flex items-center py-16 lg:py-20 lg:pl-16">
                            <p className="max-w-[760px] text-[16px] leading-8 text-white/60">
                                ISBM University's academic philosophy places learning, personal development, ethical responsibility and meaningful contribution at the heart of the educational experience.
                            </p>
                        </div>
                    </div>
                </section>

            </main>

            <Footer />
        </>
    );
}